/* Atlas v3 — CycleChart: the expanded chart for a cycle signal.
   Self-contained and size-agnostic (width/height props) — the detail sheet
   renders it at ~330×190.

   What it adds over a Spark:
   - timeframe chips sized to the signal's cadence (1M/6M/1Y/ALL daily,
     1Y/3Y/ALL monthly, 3Y/ALL quarterly), cut relative to the LAST data
     point (never the wall clock);
   - real axes: min/max value labels + three date ticks;
   - the SCORING WINDOW band: shaded min–max + dashed median of the window
     the level percentile is computed against, so "P19" is visible;
   - a dashed zero line on %-YoY series (above = growing, below = shrinking);
   - touch-scrub crosshair with a date · value readout (touchAction: none so
     scrubbing doesn't fight the sheet scroll).

   PURE view: everything derives from the passed series. */
import { useMemo, useRef, useState } from 'react'
import { Chip } from './lib'

const Mono = { fontFamily: 'var(--studio-font-mono)', fontVariantNumeric: 'tabular-nums' }

const DAY = 86400000
const WINDOW_DAYS = { '1y': 366, '3y': 1096, '4y': 1461 }

/* Timeframe chips are DERIVED from the data, not fixed per cadence — the
   series span anything from 17 months (AI usage) to 42 years (Taiwan export
   orders), so no fixed list fits. A ladder rung qualifies when the series
   has ≥8 points inside it AND it's meaningfully shorter than the full span;
   at most 4 rungs (largest kept) + ALL. Rungs that don't qualify are not
   rendered at all. */
const LADDER = [
  { key: '1M', days: 31 }, { key: '6M', days: 183 }, { key: '1Y', days: 366 },
  { key: '3Y', days: 1096 }, { key: '5Y', days: 1827 }, { key: '10Y', days: 3653 },
]
const MIN_VIEW_POINTS = 8
function deriveChips(series, lastT, spanDays) {
  const rungs = LADDER.filter((r) => {
    if (r.days >= spanDays * 0.9) return false
    let pts = 0
    for (const pt of series) if ((lastT - Date.parse(pt.p)) / DAY <= r.days && ++pts >= MIN_VIEW_POINTS) break
    return pts >= MIN_VIEW_POINTS
  }).slice(-4)
  return [...rungs, { key: 'ALL', days: Infinity }]
}
// Default = the rung closest to the scoring window (what the percentile is
// measured against), else ALL.
function defaultChip(chips, win) {
  const target = WINDOW_DAYS[win]
  if (!target) return 'ALL'
  let best = null
  for (const c of chips) {
    if (c.key === 'ALL') continue
    if (!best || Math.abs(c.days - target) < Math.abs(best.days - target)) best = c
  }
  return best?.key ?? 'ALL'
}

export const fmtVal = (v, isYoY, unit) => {
  if (v == null || !Number.isFinite(v)) return '—'
  if (isYoY) return `${v > 0 ? '+' : ''}${(Math.round(v * 10) / 10)}%`
  const a = Math.abs(v)
  const num = a >= 1000 ? Math.round(v).toLocaleString('en-US') : a >= 100 ? v.toFixed(0) : a >= 10 ? v.toFixed(1) : v.toFixed(2)
  if (unit?.endsWith('%')) return `${num}%`
  if (unit?.startsWith('$')) return `$${num}`
  return num
}

const fmtDate = (iso, grain) => {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return iso
  const d = new Date(t)
  const yy = `’${String(d.getUTCFullYear()).slice(2)}`
  if (grain === 'quarterly') return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${yy}`
  const mon = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase()
  if (grain === 'daily') return `${mon} ${d.getUTCDate()} ${yy}`
  return `${mon} ${yy}`
}

let SEQ = 0

export function CycleChart({ full, unit, width, height, up }) {
  const { series, grain, window: win, isYoY } = full
  const lastT = Date.parse(series[series.length - 1].p)
  const spanDays = (lastT - Date.parse(series[0].p)) / DAY
  const chips = useMemo(() => deriveChips(series, lastT, spanDays), [series, lastT, spanDays])

  const [tf, setTf] = useState(() => defaultChip(chips, win))
  const [scrub, setScrub] = useState(null) // index into view
  const svgRef = useRef(null)
  const [gid] = useState(() => `cyc${++SEQ}`)

  const active = chips.find((c) => c.key === tf) ?? chips[chips.length - 1]
  const activeTf = active.key

  const view = useMemo(() => {
    return active.days === Infinity ? series : series.filter((pt) => (lastT - Date.parse(pt.p)) / DAY <= active.days)
  }, [series, active, lastT])

  // Scoring-window band (what the level percentile is measured against).
  const band = useMemo(() => {
    const days = WINDOW_DAYS[win]
    if (!days) return null
    const vals = series.filter((pt) => (lastT - Date.parse(pt.p)) / DAY <= days).map((pt) => pt.v)
    if (vals.length < 3) return null
    const s = [...vals].sort((a, b) => a - b)
    return { min: s[0], max: s[s.length - 1], med: s[Math.floor(s.length / 2)] }
  }, [series, win, lastT])

  // Geometry
  const PAD = { l: 8, r: 12, t: 8, b: 16 }
  const plotW = width - PAD.l - PAD.r
  const plotH = height - PAD.t - PAD.b
  const vs = view.map((pt) => pt.v)
  const vMin = Math.min(...vs, isYoY ? 0 : Infinity)
  const vMax = Math.max(...vs, isYoY ? 0 : -Infinity)
  const range = vMax - vMin || 1
  const X = (i) => PAD.l + (view.length === 1 ? plotW / 2 : (i / (view.length - 1)) * plotW)
  const Y = (v) => PAD.t + plotH - ((v - vMin) / range) * plotH
  const path = view.map((pt, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(pt.v).toFixed(1)}`).join('')
  const area = `${path}L${X(view.length - 1).toFixed(1)},${PAD.t + plotH}L${X(0).toFixed(1)},${PAD.t + plotH}Z`
  const tone = up === false ? '--studio-danger' : '--studio-success'

  // 3 date ticks; 4 on long views (>5y) where a lone midpoint is too sparse.
  const viewSpanDays = (Date.parse(view[view.length - 1].p) - Date.parse(view[0].p)) / DAY
  const n1 = view.length - 1
  const ticks = view.length > 2
    ? (viewSpanDays > 1827
      ? [0, Math.round(n1 / 3), Math.round((2 * n1) / 3), n1]
      : [0, Math.floor(n1 / 2), n1])
    : [0, n1]

  // Scrub: pointer x → nearest view index. touchAction none = no scroll fight.
  const scrubTo = (clientX) => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    const t = (clientX - rect.left - PAD.l * (rect.width / width)) / (plotW * (rect.width / width))
    setScrub(Math.max(0, Math.min(view.length - 1, Math.round(t * (view.length - 1)))))
  }
  const cur = scrub == null ? view[view.length - 1] : view[scrub]

  return (
    <div>
      {/* timeframe chips — only rungs this series can actually fill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
        {chips.map((c) => (
          <Chip key={c.key} active={activeTf === c.key} onClick={() => { setTf(c.key); setScrub(null) }}>{c.key}</Chip>
        ))}
      </div>

      {/* readout: latest by default, scrubbed point while dragging */}
      <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', height: 14 }}>
        {scrub == null ? 'LATEST · ' : ''}{fmtDate(cur.p, grain)} · <span style={{ color: `hsl(var(${tone}))` }}>{fmtVal(cur.v, isYoY, unit)}</span>
        {!isYoY && unit && !unit.startsWith('$') && !unit.endsWith('%') ? ` ${unit}` : ''}
      </div>

      <svg ref={svgRef} width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', touchAction: 'none' }}
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); scrubTo(e.clientX) }}
        onPointerMove={(e) => { if (e.buttons) scrubTo(e.clientX) }}
        onPointerUp={() => setScrub(null)}
        onPointerCancel={() => setScrub(null)}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`hsl(var(${tone}) / 0.16)`} />
            <stop offset="100%" stopColor={`hsl(var(${tone}) / 0)`} />
          </linearGradient>
        </defs>

        {/* scoring-window band + median (clamped to the plot) */}
        {band && (() => {
          const y1 = Math.max(PAD.t, Math.min(PAD.t + plotH, Y(band.max)))
          const y2 = Math.max(PAD.t, Math.min(PAD.t + plotH, Y(band.min)))
          const ym = Y(band.med)
          return (
            <g>
              <rect x={PAD.l} y={y1} width={plotW} height={Math.max(0, y2 - y1)} fill="hsl(var(--studio-text-subtle) / 0.07)" />
              {ym > PAD.t && ym < PAD.t + plotH && (
                <line x1={PAD.l} x2={PAD.l + plotW} y1={ym} y2={ym} stroke="hsl(var(--studio-text-subtle) / 0.35)" strokeWidth="0.75" strokeDasharray="3 3" />
              )}
            </g>
          )
        })()}

        {/* zero line for growth-rate series */}
        {isYoY && vMin < 0 && vMax > 0 && (
          <g>
            <line x1={PAD.l} x2={PAD.l + plotW} y1={Y(0)} y2={Y(0)} stroke="hsl(var(--studio-border))" strokeWidth="0.75" strokeDasharray="2 3" />
            <text x={PAD.l + plotW + 2} y={Y(0) + 2.5} style={{ ...Mono, fontSize: 7 }} fill="hsl(var(--studio-text-subtle))">0</text>
          </g>
        )}

        <path d={area} fill={`url(#${gid})`} />
        <path d={path} fill="none" stroke={`hsl(var(${tone}))`} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />

        {/* min / max value labels */}
        <text x={PAD.l} y={PAD.t + 7} style={{ ...Mono, fontSize: 7.5 }} fill="hsl(var(--studio-text-subtle))">{fmtVal(vMax, isYoY, unit)}</text>
        <text x={PAD.l} y={PAD.t + plotH - 3} style={{ ...Mono, fontSize: 7.5 }} fill="hsl(var(--studio-text-subtle))">{fmtVal(vMin, isYoY, unit)}</text>

        {/* date ticks */}
        {ticks.map((i, k) => (
          <text key={i} x={X(i)} y={height - 4}
            textAnchor={k === 0 ? 'start' : k === ticks.length - 1 ? 'end' : 'middle'}
            style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.05em' }} fill="hsl(var(--studio-text-subtle))">
            {fmtDate(view[i].p, grain)}
          </text>
        ))}

        {/* crosshair + point */}
        {scrub != null && (
          <line x1={X(scrub)} x2={X(scrub)} y1={PAD.t} y2={PAD.t + plotH} stroke="hsl(var(--studio-accent) / 0.55)" strokeWidth="0.75" />
        )}
        <circle cx={X(scrub == null ? view.length - 1 : scrub)} cy={Y(cur.v)} r="2.4" fill={`hsl(var(${tone}))`} />
      </svg>
    </div>
  )
}

